import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useGlobalSearchParams, useRootNavigationState, useSegments } from 'expo-router';
import {
  normalizeRumViewName,
  startBrowserRumView,
  startNativeRumView,
  stopNativeRumView,
} from '@/lib/datadog';
import { normalizeStringParam } from '@/lib/routes';

export function DatadogRouteTracker() {
  const rootNavigationState = useRootNavigationState();
  const segments = useSegments();
  const params = useGlobalSearchParams<{
    id?: string | string[];
    season?: string | string[];
    week?: string | string[];
    mmRound?: string | string[];
  }>();
  const activeViewKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rootNavigationState?.key) {
      return;
    }

    const nextPath =
      segments.length > 0
        ? `/${segments.filter((segment) => !segment.startsWith('(')).join('/')}`
        : '/';
    const nextViewName = normalizeRumViewName(nextPath);
    const viewAttributes = {
      pathname: nextPath,
      ...(normalizeStringParam(params.id) ? { leagueId: normalizeStringParam(params.id) } : {}),
      ...(normalizeStringParam(params.season) ? { seasonId: normalizeStringParam(params.season) } : {}),
      ...(normalizeStringParam(params.week) ? { weekId: normalizeStringParam(params.week) } : {}),
      ...(normalizeStringParam(params.mmRound) ? { mmRound: normalizeStringParam(params.mmRound) } : {}),
    };

    if (Platform.OS === 'web') {
      startBrowserRumView(nextViewName, viewAttributes);
      return;
    }

    if (activeViewKeyRef.current === nextPath) {
      return;
    }

    if (activeViewKeyRef.current) {
      stopNativeRumView(activeViewKeyRef.current, { pathname: activeViewKeyRef.current });
    }

    activeViewKeyRef.current = nextPath;
    startNativeRumView(nextPath, nextViewName, viewAttributes);

    return () => {
      if (activeViewKeyRef.current === nextPath) {
        stopNativeRumView(nextPath, viewAttributes);
      }
    };
  }, [params.id, params.mmRound, params.season, params.week, rootNavigationState?.key, segments]);

  return null;
}
